import {
  BanknotesIcon,
  ClipboardDocumentCheckIcon,
  IdentificationIcon,
  RectangleGroupIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { CurrencyDollarIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import BasicProjectInfo from '@/components/Sherpa/Projects/NewProjectForm/BasicProjectInfo';
import ProjectSources from '@/components/Sherpa/Projects/NewProjectForm/ProjectSources';
import ProjectTemplateInfo from '@/components/Sherpa/Projects/NewProjectForm/ProjectTemplateInfo';
import ProjectUses from '@/components/Sherpa/Projects/NewProjectForm/ProjectUses';
import FormReview from '@/components/Sherpa/Projects/NewProjectForm/Review';
import Confirm from '@/components/UI/Modal/Confirm';
import { useEnsureCurrentWorkspace } from '@/hooks/useEnsureCurrentWorkspace';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import { clearProjectBaselineInfo } from '../../../../store/Projects/Project/BaselineProjectInfo';
import { clearProjectBudget } from '../../../../store/Projects/Project/ProjectBudget';
import { clearProjectTemplate } from '../../../../store/Projects/Project/ProjectTemplate';
import styles from './NewProject.module.css';

const formNav = [
  {
    name: 'Baseline Project Info',
    key: 'baseline',
    icon: IdentificationIcon,
  },
  { name: 'Project Template', key: 'template', icon: RectangleGroupIcon },
  { name: 'Project Sources', key: 'sources', icon: CurrencyDollarIcon },
  { name: 'Project Uses', key: 'uses', icon: BanknotesIcon },
  { name: 'Review', key: 'review', icon: ClipboardDocumentCheckIcon },
];

export const NewProjectForm = () => {
  const [currentStep, updateStep] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  useEnsureCurrentWorkspace();

  const resetProjectForm = () => {
    dispatch(clearProjectBaselineInfo());
    dispatch(clearProjectBudget());
    dispatch(clearProjectTemplate());
  };

  useEffect(() => {
    resetProjectForm();
  }, [resetProjectForm]);

  const handleProgress = (type: string) => {
    if (type === 'FORWARD') {
      return updateStep(currentStep + 1);
    }
    if (type === 'BACK') {
      return updateStep(currentStep - 1);
    }
    return;
  };

  const handleStep = (step: number) => {
    updateStep(step);
  };

  const closeFormHandler = () => {
    setShowCancelConfirm(true);
  };

  const onCancelProjectCreation = async () => {
    setShowCancelConfirm(false);
    navigate(buildRoute(AppRoutes.SHERPA_PROJECTS));
    resetProjectForm();
  };

  const onStayProjectCreation = () => {
    setShowCancelConfirm(false);
  };

  return (
    <div className='max-w-6xl mx-auto relative'>
      <h1>New Project</h1>
      {showCancelConfirm && (
        <Confirm
          title='Cancel project'
          message={
            'You are about to cancel the project. You would loose all the entered data on this form. Are you sure you want to cancel ?'
          }
          acceptButtonLabel='Cancel '
          declineButtonLabel='Stay'
          accept={onCancelProjectCreation}
          decline={onStayProjectCreation}
        />
      )}
      <div className='flex mt-4 bg-white rounded-md shadow-xl'>
        <ul className={styles.menu}>
          {formNav.map((nav, i) => (
            <li key={nav.key} className={` ${currentStep === i && styles.selected}`}>
              <nav.icon className='h-6 w-6 text-gray-400' />
              <span className='sm:hidden md:block'>{nav.name}</span>
            </li>
          ))}
        </ul>
        <div className={'relative p-10 w-full border-l-2'}>
          <span
            className='absolute text-neutralColor-normal top-4 right-4 hover:text-neutralColor-dark cursor-pointer'
            onClick={closeFormHandler}
          >
            <XCircleIcon className='h-6 w-6' />
          </span>

          {currentStep === 0 && <BasicProjectInfo handleNext={handleProgress} />}
          {currentStep === 1 && <ProjectTemplateInfo handleProgress={handleProgress} />}
          {currentStep === 2 && <ProjectSources handleProgress={handleProgress} />}
          {currentStep === 3 && <ProjectUses handleProgress={handleProgress} />}
          {currentStep === 4 && <FormReview handleStep={handleStep} />}
        </div>
      </div>
    </div>
  );
};
